import { create } from 'zustand';
import { TNote, TNoteMessage } from '../types';

type State = {
  notes: TNote[];
  selectedNote: TNote | null;
};

type Actions = {
  setNotes: (notes: TNote[] | ((prev: TNote[]) => TNote[])) => void;
  selectNote: (note: number | null) => void;
  updateNote: (id: number, note: Partial<TNote>) => TNote | null;
  deleteNote: (id: number) => void;

  // messages
  updateMessage: (noteId: number, messageId: number, message: Partial<TNoteMessage>) => void;
  deleteMessage: (noteId: number, messageId: number) => void;
};

export const useNotesStore = create<State & { actions: Actions }>((set, get) => ({
  notes: [],
  selectedNote: null,
  actions: {
    setNotes: (notesGetter) => {
      const newNotes = typeof notesGetter === 'function' ? notesGetter(get().notes) : notesGetter;

      const { selectedNote } = get();
      const newSelectedNote = newNotes.find((n) => n.id === selectedNote?.id);

      set({ notes: newNotes, selectedNote: newSelectedNote });
    },
    selectNote: (noteId) => {
      if (!noteId) {
        set({ selectedNote: null });
        return;
      }

      set((state) => {
        const selectedNote = state.notes.find((note) => note.id === noteId);
        return { selectedNote };
      });
    },
    updateNote: (noteId, newNote) => {
      const { notes, selectedNote } = get();

      const note = notes.find((n) => n.id === noteId);
      if (!note) return;

      const updatedNote = {
        ...note,
        ...newNote,
      };

      set({
        notes: notes.map((n) => {
          if (n.id === noteId) {
            return updatedNote;
          }
          return n;
        }),
        selectedNote: selectedNote?.id === noteId ? updatedNote : selectedNote,
      });

      return updatedNote;
    },

    deleteNote: (noteId) => {
      const { notes, selectedNote } = get();

      set({
        notes: notes.filter((n) => n.id !== noteId),
        selectedNote: selectedNote?.id === noteId ? null : selectedNote,
      });
    },

    // Message
    updateMessage: (noteId, messageId, message) => {
      const { notes } = get();
      const targetNote = notes.find((n) => n.id === noteId);
      const messageIndex = targetNote?.messages.findIndex((m) => m.id === messageId);

      if (!targetNote || messageIndex === -1) {
        // console.debug('Could not find note message to udate');
        return;
      }

      const updateNoteWithMessage = {
        ...targetNote,
        messages: targetNote.messages.map((m, index) => {
          if (index === messageIndex) {
            return { ...m, ...message };
          }
          return m;
        }),
      };

      set((state) => {
        return {
          notes: state.notes.map((n) => {
            return n.id === noteId ? updateNoteWithMessage : n;
          }),
          selectedNote:
            state.selectedNote?.id === noteId ? updateNoteWithMessage : state.selectedNote,
        };
      });
    },

    deleteMessage(noteId, messageId) {
      const { notes } = get();
      const targetNote = notes.find((n) => n.id === noteId);
      const messageIndex = targetNote?.messages.findIndex((m) => m.id === messageId);

      if (!targetNote || messageIndex === -1) return;

      const updatedNoteWithoutMessage = {
        ...targetNote,
        messages: targetNote.messages.filter((_m, index) => index !== messageIndex),
      };

      set((state) => ({
        notes: state.notes.map((n) => {
          return n.id === noteId ? updatedNoteWithoutMessage : n;
        }),
        selectedNote:
          state.selectedNote?.id === noteId ? updatedNoteWithoutMessage : state.selectedNote,
      }));
    },
  },
}));
